import { REMINDER_EVENT_TYPES } from '@/constants/device';
import { CloseOutlined } from '@ant-design/icons';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Divider, Input, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import Contact from './Contact';
import { AddIcon, CloseIcon, NewContactsIcon } from './Icons';
import Loading from './Loading';

const getSelectedListStyle = (onlySelected) => {
  return {
    ...(onlySelected ? {} : { maxHeight: '50%' }),
  };
};

const ContactSelectList = ({
  style,
  className,
  fetchingContacts = false,
  displayOnlySelected = false,
  selectedListHeader = 'Included Contacts',
  selectionListHeader = 'Add Contacts',
  selectedContactList = [],
  selectionContactList = [],
  showAddIcon = true,
  showRemoveIcon = true,
  onAddClick = () => {},
  onRemoveClick = () => {},
  onAddAllContacts = () => {},
  selectedContactProps = {},
  selectionContactProps = {},
  showRelationship = true,
  showAddIconCondition = () => true,
  showRemoveIconCondition = () => true,
  assignStaffAction = false,
  selectedUser = '',
  hubRecord = {},
  starteventAction = false,
  principalRecord = {},
  trans,
  eventType,
  eventRecord = {},
}) => {
  const isEmpty = assignStaffAction
    ? selectedContactList !== null
    : Array.isArray(selectedContactList) && selectedContactList.length !== 0;
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchApplied = !!searchText.trim();

  // Compute displayed contacts (excluding already selected ones)
  const displayedContacts = useMemo(() => {
    return selectionContactList.filter(
      (contact) => !selectedContactList.some((selected) => selected.useruuid === contact.useruuid)
    );
  }, [selectionContactList, selectedContactList]);

  // Compute filtered contacts based on search text
  const filteredContacts = useMemo(() => {
    if (!searchApplied) {
      return displayedContacts;
    }

    return displayedContacts.filter((contact) =>
      getUsername(contact).toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchApplied, searchText, displayedContacts]);

  const handleClearSearch = () => {
    setSearchText('');
  };
  return (
    <div style={style} className={`contact-selection-list-wrapper ${className ?? ''}`}>
      {/* Add Participants label with close icon */}
      {showAllContacts && eventType !== REMINDER_EVENT_TYPES.VISIT && (
        <div className="add-participant">
          <span className="add-participant-label">{trans('ADDOTHERPARTICIPANTS')}</span>
          <span className="remove-expanded-list">
            <CloseIcon
              className="close-icon"
              onClick={() => {
                setShowAllContacts(false); // Revert to showing only 4 contacts
              }}
            />
          </span>
        </div>
      )}

      {/* Selected List Header */}
      {selectedListHeader && (
        <label className="selected-header">
          {!showAllContacts ? selectedListHeader : `${trans('PARTICIPANTS')} ${trans('ADDED')}`}
        </label>
      )}
      <div
        className={`${isEmpty ? 'selected-list-container' : 'selected-list-empty'}`}
        style={getSelectedListStyle(displayOnlySelected)}
      >
        {selectedContactList?.map((item) => (
          <Contact
            key={'selected_' + item.useruuid}
            className="primary"
            contact={item}
            hubRecord={hubRecord}
            starteventAction={starteventAction}
            actionIcon={
              showRemoveIcon &&
              showRemoveIconCondition(item) &&
              !(
                eventType === REMINDER_EVENT_TYPES.VISIT && item.useruuid === eventRecord?.useruuid
              ) && <CloseIcon className="close-icon" onClick={() => onRemoveClick(item)} />
            }
            showRelationship={true}
            {...selectedContactProps}
            principalRecord={principalRecord}
          />
        ))}
      </div>

      {starteventAction && <Divider className="contacts-divider" />}

      {!displayOnlySelected && (
        <>
          {selectionListHeader && (
            <label className="selection-list-header">{selectionListHeader}</label>
          )}

          {fetchingContacts ? (
            <Loading />
          ) : (
            <div className="selection-list-container">
              <div className="account-name">
                <NewContactsIcon />
                <span className="username">
                  <span className="username-truncate">
                    <Tooltip
                      title={selectedUser}
                    >{`${trans('F_ORM')} ${selectedUser}${trans('SCONTACTS_L')}`}</Tooltip>
                  </span>
                </span>
              </div>
              {/* "Find a contact" label and search input */}
              <div className="contact-search-container">
                <label className="find-a-contact">{trans('FINDACONTACT')}</label>
                <div className="contact-input-search">
                  <Input
                    className="search-input"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={trans('SEARCH_ACONTACT_L')} // Placeholder text for the search input
                    suffix={
                      searchApplied ? (
                        <CloseOutlined onClick={handleClearSearch} title={trans('CLEARSEARCH')} />
                      ) : (
                        <></>
                      )
                    }
                  />
                </div>
              </div>
              <div className="contact-list-scroll">
                {searchApplied && filteredContacts.length === 0 ? (
                  <div className="no-filtered-contacts-container">
                    <span className="no-contact-available">{trans('NOCONTACTSFOUND')}</span>
                  </div>
                ) : (
                  (searchApplied ? filteredContacts : displayedContacts).map((item) => (
                    <Contact
                      key={'contact_' + item.useruuid}
                      onClick={!item.disabled && (item.onAddIconClick ?? onAddClick)}
                      contact={item}
                      hubRecord={hubRecord}
                      starteventAction={starteventAction}
                      actionIcon={
                        item.addIcon ??
                        (showAddIcon && showAddIconCondition(item) && (
                          <AddIcon className="add-icon" />
                        ))
                      }
                      showRelationship={true}
                      {...selectionContactProps}
                      principalRecord={principalRecord}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ContactSelectList.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  fetchingContacts: PropTypes.bool,
  assignStaffAction: PropTypes.bool,
  displayOnlySelected: PropTypes.bool,
  selectedListHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  selectionListHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  selectedContactList: PropTypes.arrayOf(PropTypes.object),
  selectionContactList: PropTypes.arrayOf(PropTypes.object),
  showAddIcon: PropTypes.bool,
  showRemoveIcon: PropTypes.bool,
  onAddClick: PropTypes.func,
  onRemoveClick: PropTypes.func,
  selectedContactProps: PropTypes.object,
  selectionContactProps: PropTypes.object,
  showRelationship: PropTypes.bool,
  onAddAllContacts: PropTypes.func,
  principalRecord: PropTypes.object,
  eventType: PropTypes.string,
  eventRecord: PropTypes.object,
};

export default ContactSelectList;
